import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Bloco de cadastro: 4 a 6 campos com nome e uma linha dizendo onde aquele dado aparece depois
 * ("Aparece no cabeçalho e na qualificação das partes."). Formulário corrido de 15 campos sem divisão
 * é reprovado. Ação destrutiva fica num bloco à parte, separada do salvar.
 */
export interface FormBlockProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode
  /** Onde o dado aparece depois. */
  note?: ReactNode
  trailing?: ReactNode
  children: ReactNode
}

export function FormBlock({
  title,
  note,
  trailing,
  children,
  className,
  ...props
}: FormBlockProps) {
  return (
    <section
      data-slot="form-block"
      className={cn('border-hairline bg-surface max-desk:px-4 border px-6 py-5', className)}
      {...props}
    >
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h2 className="eyebrow">{title}</h2>
          {note ? (
            <p className="font-body text-secondary mt-[6px] text-[12px] leading-[1.6] font-light">
              {note}
            </p>
          ) : null}
        </div>
        {trailing}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  )
}

/** Grade de campos: uma coluna no celular, duas a partir de `tablet`. Um campo pode ocupar a linha com `col-span-full`. */
export function FieldGrid({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="field-grid"
      className={cn('tablet:grid-cols-2 grid grid-cols-1 gap-x-5 gap-y-4', className)}
      {...props}
    />
  )
}

/** Coluna de leitura e formulário: máximo 640px (ou 840px com `wide`), centrada no conteúdo (T36). */
export function ReadingColumn({
  wide,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement> & { wide?: boolean }) {
  return (
    <div
      data-slot="reading-column"
      className={cn('mx-auto w-full', wide ? 'max-w-reading-wide' : 'max-w-reading', className)}
      {...props}
    />
  )
}
